import React, { useEffect, useRef, useState } from "react";

import { Button, Icon, ImagePreview, PlainButton, classnames } from "@culturehq/components";
import { isSignedIn } from "@culturehq/client";

import { useIsMobile } from "app/hooks";
import { t } from "app/utils";
import IconsPaginationSimple from "components/common/IconsPaginationSimple";

import VideoStudioContext from "./video-studio/contexts/VideoStudioContext";

const ImageSlider = ({
  currentBody,
  currentImageIndex,
  currentStory,
  getSelectedMediaUrl,
  image,
  index,
  mediaData,
  onOpenCropImage,
  onMediaEdited,
  profile,
  selectedPrompt
}) => {
  const isMobile = useIsMobile();
  const isLoggedIn = isSignedIn();
  const [mediaUrl, setMediaUrl] = useState(getSelectedMediaUrl(image));
  const [dimensions, setDimensions] = useState({ height: null, width: null });

  useEffect(
    () => {
      if (image && image instanceof File) {
        const imageElement = new Image();
        imageElement.src = URL.createObjectURL(image);
        imageElement.onload = () => {
          setDimensions({ height: imageElement.height, width: imageElement.width });
        };
      }
    }, [image]
  );

  useEffect(
    () => {
      setMediaUrl(getSelectedMediaUrl(image));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [image]
  );

  const handleMediaEdited = value => {
    onMediaEdited(index, value);
  };

  if (!isMobile && mediaData[index] && index === currentImageIndex) {
    return (
      <>
        <VideoStudioContext
          playerCreatomateJson={mediaData[index]}
          mode="player"
          story={currentStory}
        />
        <div className="VideoOptionContainer">
          <div className="VideoOptions">
            <VideoStudioContext
              onMediaEdited={handleMediaEdited}
              story={{ media: { mediaJson: mediaData[index] } }}
              mediaVersionHistory={[{ additionalData: mediaData[index] }]}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="chq-ffd--bg-img--img StoryMedia-Background"
        style={{ backgroundImage: `url("${mediaUrl}")` }}
      />
      <label className="chq-ffd chq-ffd--bg-img image-container">
        <div className="chq-ffd--im" style={{ overflow: "hidden" }}>
          <div className="user-selected-image">
            <ImagePreview
              alt={image.alt_description || image.name}
              image={mediaUrl}
              preview={mediaUrl}
              imageAsBackground
            />
            <div className="VideoOptionContainer">
              {isLoggedIn && !isMobile && (image instanceof File || image.urls || image) && (
                <div className="VideoOptions">
                  <VideoStudioContext
                    dimensions={dimensions}
                    onMediaEdited={handleMediaEdited}
                    story={{
                      creator: currentStory?.creator || profile,
                      question: selectedPrompt,
                      media: image,
                      body: currentBody
                    }}
                    mediaVersionHistory={[{ additionalData: mediaData[index] }]}
                  />
                </div>
              )}
              <div className="ImageCrop">
                {(image instanceof File || image.urls || image) && (
                  <PlainButton aria-label="Crop Image" onClick={() => onOpenCropImage(index)}>
                    <Icon icon="crop" />
                  </PlainButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </label>
    </>
  );
};

const StoryImageSlider = ({
  currentBody,
  currentStory,
  getSelectedMediaUrl,
  images,
  mediaData,
  onOpenCropImage,
  onMediaEdited,
  onRemoveImage,
  profile,
  selectedPrompt
}) => {
  const [currentImage, setCurrentImage] = useState(0);
  const quantity = images?.length;

  if (!Array.isArray(images) || quantity === 0) return <></>;

  const nextImage = () => {
    setCurrentImage(currentImage === quantity - 1 ? 0 : currentImage + 1);
  };

  const previousImage = () => {
    setCurrentImage(currentImage === 0 ? quantity - 1 : currentImage - 1);
  };

  const handleRemoveImage = () => {
    if (currentImage === images.length - 1) {
      setCurrentImage(0);
    }
    onRemoveImage(currentImage);
  };

  return (
    <div className="ImageSlider">
      {images.map((image, index) => (
        <div
          key={String(image.id || index)}
          className={classnames(
            "chq-ffd--bg-img-container",
            "StoryMedia",
            currentImage === index ? "active" : undefined
          )}
        >
          <ImageSlider
            currentBody={currentBody}
            currentImageIndex={currentImage}
            currentStory={currentStory}
            getSelectedMediaUrl={getSelectedMediaUrl}
            image={image}
            index={index}
            mediaData={mediaData}
            onOpenCropImage={onOpenCropImage}
            onMediaEdited={onMediaEdited}
            profile={profile}
            selectedPrompt={selectedPrompt}
          />
        </div>
      ))}
      <div className="ImageSlider-Delete">
        <Button
          aria-label="Delete Image"
          className="VideoStudio-Btn"
          onClick={handleRemoveImage}
        >
          <Icon icon="trash-a" />
        </Button>
      </div>
      {images.length > 1 && (
        <>
          <div className="ImageSlider-Pagination">
            <div className="navigation">
              <IconsPaginationSimple
                currentPage={currentImage + 1}
                totalPages={images.length}
              />
            </div>
          </div>
          <div>
            <PlainButton
              aria-label={t("Previous selected image")}
              className="image-slider-arrow image-slider-arrow-left"
              onClick={previousImage}
            >
              <Icon icon="chevron-left" />
            </PlainButton>
          </div>
          <div>
            <PlainButton
              aria-label={t("Next selected image")}
              className="image-slider-arrow image-slider-arrow-right"
              onClick={nextImage}
            >
              <Icon icon="chevron-right" />
            </PlainButton>
          </div>
        </>
      )}
    </div>
  );
};

export default StoryImageSlider;
